// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#fleet-health-reporting
import * as React from 'react';

import { relativeTime } from '@/components/greenlight/relativeTime';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import type {
  FleetContactDelivery,
  FleetContactDeliveryStatus,
  FleetEnrollment,
  FleetEnrollmentStatus,
  FleetEntitlementSignal,
  FleetEntitlementState,
  FleetKeyRotation,
  FleetKeyRotationStatus,
  FleetReportDelivery,
  FleetReportDeliveryStatus,
  FleetReportingEnforcement,
  FleetReportingState,
} from '@greenlight/shared';

const DELIVERY_BADGE: Record<
  FleetReportDeliveryStatus,
  { variant: StatusBadgeVariant; label: string }
> = {
  pending: { variant: 'pending', label: 'Queued' },
  sent: { variant: 'active', label: 'Sent' },
  failed_retryable: { variant: 'degraded', label: 'Retrying' },
  failed_permanent: { variant: 'failed', label: 'Failed' },
};

const ENROLLMENT_BADGE: Record<
  FleetEnrollmentStatus,
  { variant: StatusBadgeVariant; label: string }
> = {
  pending: { variant: 'pending', label: 'Enrolling' },
  enrolled: { variant: 'active', label: 'Enrolled' },
  failed: { variant: 'failed', label: 'Enrollment failed' },
};

const ROTATION_BADGE: Record<
  FleetKeyRotationStatus,
  { variant: StatusBadgeVariant; label: string }
> = {
  pending: { variant: 'pending', label: 'Rotating key' },
  rotated: { variant: 'active', label: 'Key rotated' },
  failed: { variant: 'failed', label: 'Rotation failed' },
};

const CONTACT_BADGE: Record<
  FleetContactDeliveryStatus,
  { variant: StatusBadgeVariant; label: string }
> = {
  pending: { variant: 'pending', label: 'Sending' },
  sent: { variant: 'active', label: 'Sent' },
  failed: { variant: 'failed', label: 'Send failed' },
};

const ENFORCEMENT_BADGE: Record<
  FleetReportingState,
  { variant: StatusBadgeVariant; label: string }
> = {
  ok: { variant: 'active', label: 'Reporting' },
  warning: { variant: 'degraded', label: 'Delivery overdue' },
  degraded: { variant: 'failed', label: 'Building disabled' },
};

const LICENSE_BADGE: Record<FleetEntitlementState, { variant: StatusBadgeVariant; label: string }> =
  {
    entitled: { variant: 'active', label: 'Licensed' },
    grace: { variant: 'degraded', label: 'Ending soon' },
    lapsed: { variant: 'failed', label: 'Lapsed' },
    unknown: { variant: 'pending', label: 'Not asserted' },
  };

/**
 * Plain-language readings of the reasons Shift sends today. A reason this
 * release predates falls through to the raw value rather than being hidden —
 * an operator on a support call needs the word Shift actually sent.
 *
 * No wording here states a trial length: docs/26 §Trial and entitlement copy
 * holds that back until it is settled product policy.
 */
const LICENSE_REASON: Record<string, string> = {
  trial_active: 'Free trial is running',
  trial_ending: 'Free trial is about to end',
  trial_expired: 'Free trial has ended',
  license_recorded: 'Covered by a recorded contract',
  no_terms_recorded: 'Shift has no terms recorded for this install',
  installation_disabled: 'Reporting is disabled for this install',
  payment_current: 'Billing account is in good standing',
  manual_terms: 'Billing account is on terms invoiced by Shift',
  payment_past_due: 'A payment failed; Shift is retrying until the date shown',
  payment_failed: 'Payment did not go through by the deadline',
  standing_unknown: 'Shift can’t confirm the billing account’s standing right now',
  payment_method_pending: 'A payment method is due by the date shown',
  payment_method_due: 'A payment method is due by the date shown; new building pauses after it',
  payment_method_missing: 'No payment method was saved by the date it was due',
  payment_method_on_file: 'A payment method is on file',
  internal_install: 'A Shift-internal install, never billed',
};

/**
 * Fleet reporting to Shift — always on (docs/11 §Fleet Health Reporting):
 * enrollment state with the exact identity payload sent to the fleet monitor,
 * and the latest report delivery with the exact JSON payload sent, so IT sees
 * precisely what leaves the install (AC-OBS-44).
 */
export function FleetReportingSection({ pollMs }: { pollMs: number }): React.ReactElement {
  const { data, isLoading, isError } = api.health.latestFleetReport.useQuery(
    ['health-fleet-report'],
    undefined,
    {
      queryKey: ['health-fleet-report'],
      refetchInterval: pollMs,
    },
  );

  const body = data?.status === 200 ? data.body : undefined;

  return (
    <SectionCard title="Fleet reporting to Shift">
      {isError ? (
        <Alert variant="destructive" data-testid="fleet-reporting-error">
          <AlertTitle>Couldn't load reporting status</AlertTitle>
          <AlertDescription>
            Something went wrong fetching the fleet reporting status. Retry in a moment.
          </AlertDescription>
        </Alert>
      ) : isLoading || body === undefined ? (
        <Skeleton className="h-24 w-full rounded-gl-lg" data-testid="fleet-reporting-loading" />
      ) : (
        <div className="space-y-4">
          <EnforcementDetail enforcement={body.enforcement} />
          <LicenseDetail entitlement={body.entitlement} />
          <EnrollmentDetail enrollment={body.enrollment} />
          <RotationDetail rotation={body.rotation} />
          <DeliveryDetail delivery={body.delivery} />
          <ContactDetail contact={body.contact} />
        </div>
      )}
    </SectionCard>
  );
}

/**
 * Whether reporting failures have started costing this install anything
 * (docs/11 §Fleet reporting enforcement). Leads the section because it is the
 * only part with consequences: below it is diagnostic detail explaining why.
 */
function EnforcementDetail({
  enforcement,
}: {
  enforcement: FleetReportingEnforcement;
}): React.ReactElement {
  const badge = ENFORCEMENT_BADGE[enforcement.state];
  return (
    <dl data-testid="fleet-enforcement" data-state={enforcement.state}>
      <Row label="Reporting status">
        <StatusBadge variant={badge.variant}>{badge.label}</StatusBadge>
      </Row>
      <Row label="Last successful delivery">
        {enforcement.last_successful_delivery_at === null
          ? 'Never'
          : relativeTime(enforcement.last_successful_delivery_at)}
      </Row>
      {enforcement.state === 'warning' && enforcement.degrade_at !== null && (
        <Row label="Building disabled on">
          {new Date(enforcement.degrade_at).toLocaleDateString()}
        </Row>
      )}
      {enforcement.state === 'degraded' && (
        <Row label="Blocked since">{relativeTime(enforcement.state_since)}</Row>
      )}
    </dl>
  );
}

/**
 * What Shift last asserted about this install's license, verbatim — the answer
 * to "when does our trial end?" without asking Shift, which is the gap this
 * closes (docs/26 §Trial and entitlement copy).
 *
 * `effective_until` is a UTC calendar day, so it is printed as sent rather than
 * localized: shifting it into the viewer's timezone can move a boundary date by
 * a day, and this is the date enforcement is measured against.
 */
function LicenseDetail({
  entitlement,
}: {
  // `undefined` as well as `null`: this page is what IT opens when the install
  // is misbehaving, so a control plane that answered without the field must
  // cost this one block rather than the whole page.
  entitlement: FleetEntitlementSignal | null | undefined;
}): React.ReactElement {
  if (entitlement === null || entitlement === undefined) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="fleet-entitlement-empty">
        Shift has not stated a license for this install yet. Reporting is how Shift confirms it, and
        the next report is within 15 minutes.
      </p>
    );
  }
  const badge = LICENSE_BADGE[entitlement.state];
  return (
    <dl data-testid="fleet-entitlement" data-state={entitlement.state}>
      <Row label="License">
        <StatusBadge variant={badge.variant}>{badge.label}</StatusBadge>
      </Row>
      {entitlement.effective_until !== null && (
        <Row label="Licensed through">{entitlement.effective_until}</Row>
      )}
      <Row label="Reason">{LICENSE_REASON[entitlement.reason] ?? entitlement.reason}</Row>
      <Row label="Confirmed by Shift">{relativeTime(entitlement.received_at)}</Row>
    </dl>
  );
}

function EnrollmentDetail({
  enrollment,
}: {
  enrollment: FleetEnrollment | null;
}): React.ReactElement {
  if (enrollment === null) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="fleet-enrollment-empty">
        This install enrolls itself with the Shift fleet monitor on the next reporting tick — within
        15 minutes. Reporting is always on.
      </p>
    );
  }
  const badge = ENROLLMENT_BADGE[enrollment.status];
  return (
    <div className="space-y-3" data-testid="fleet-enrollment">
      <dl>
        <Row label="Enrollment">
          <StatusBadge variant={badge.variant}>{badge.label}</StatusBadge>
        </Row>
        {enrollment.enrolled_at !== null && (
          <Row label="Enrolled at">{relativeTime(enrollment.enrolled_at)}</Row>
        )}
        {enrollment.status !== 'enrolled' && enrollment.next_attempt_at !== null && (
          <Row label="Next attempt">{new Date(enrollment.next_attempt_at).toLocaleString()}</Row>
        )}
        {enrollment.attempt_count > 1 && <Row label="Attempts">{enrollment.attempt_count}</Row>}
        {enrollment.error_message !== null && enrollment.status !== 'enrolled' && (
          <Row label="Last error">
            <span className="text-[hsl(var(--status-red-text))]">{enrollment.error_message}</span>
          </Row>
        )}
      </dl>
      <details className="group">
        <summary className="cursor-pointer text-sm font-medium text-foreground select-none">
          Exact enrollment payload sent to Shift
        </summary>
        <pre
          data-testid="fleet-enrollment-payload"
          className="mt-2 max-h-80 overflow-auto rounded-gl-lg border border-border bg-muted/40 p-3 text-xs text-foreground"
        >
          {JSON.stringify(enrollment.enrollment_payload, null, 2)}
        </pre>
      </details>
    </div>
  );
}

/**
 * State of the latest self-service reporting-key rotation cycle. Most of the
 * time no rotation has ever run (keys rotate every 90 days), so null renders
 * nothing rather than a permanent empty row. Reports keep flowing on the old
 * key while a rotation retries, so a failure here is a warning, not an outage.
 */
function RotationDetail({
  rotation,
}: {
  rotation: FleetKeyRotation | null;
}): React.ReactElement | null {
  if (rotation === null) return null;
  const badge = ROTATION_BADGE[rotation.status];
  return (
    <dl data-testid="fleet-key-rotation">
      <Row label="Key rotation">
        <StatusBadge variant={badge.variant}>{badge.label}</StatusBadge>
      </Row>
      {rotation.rotated_at !== null && (
        <Row label="Rotated at">{relativeTime(rotation.rotated_at)}</Row>
      )}
      {rotation.status !== 'rotated' && rotation.next_attempt_at !== null && (
        <Row label="Next attempt">{new Date(rotation.next_attempt_at).toLocaleString()}</Row>
      )}
      {rotation.attempt_count > 1 && <Row label="Attempts">{rotation.attempt_count}</Row>}
      {rotation.error_message !== null && rotation.status !== 'rotated' && (
        <Row label="Last error">
          <span className="text-[hsl(var(--status-red-text))]">{rotation.error_message}</span>
        </Row>
      )}
    </dl>
  );
}

function DeliveryDetail({
  delivery,
}: {
  delivery: FleetReportDelivery | null;
}): React.ReactElement {
  if (delivery === null) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="fleet-reporting-empty">
        No report delivery recorded yet. The first window's report is recorded within 15 minutes.
      </p>
    );
  }
  const badge = DELIVERY_BADGE[delivery.status];
  return (
    <div className="space-y-3">
      <dl>
        <Row label="Latest delivery">
          <StatusBadge variant={badge.variant}>{badge.label}</StatusBadge>
        </Row>
        <Row label="Report window">
          {new Date(delivery.window_start).toLocaleTimeString()} –{' '}
          {new Date(delivery.window_end).toLocaleTimeString()}
        </Row>
        {delivery.sent_at !== null && <Row label="Sent at">{relativeTime(delivery.sent_at)}</Row>}
        {delivery.status === 'failed_retryable' && delivery.next_attempt_at !== null && (
          <Row label="Next retry">{new Date(delivery.next_attempt_at).toLocaleString()}</Row>
        )}
        {delivery.attempt_count > 1 && <Row label="Attempts">{delivery.attempt_count}</Row>}
        {delivery.error_message !== null && delivery.status !== 'sent' && (
          <Row label="Last error">
            <span className="text-[hsl(var(--status-red-text))]">{delivery.error_message}</span>
          </Row>
        )}
      </dl>
      <details className="group">
        <summary className="cursor-pointer text-sm font-medium text-foreground select-none">
          Exact payload sent to Shift
        </summary>
        <pre
          data-testid="fleet-reporting-payload"
          className="mt-2 max-h-80 overflow-auto rounded-gl-lg border border-border bg-muted/40 p-3 text-xs text-foreground"
        >
          {JSON.stringify(delivery.report_payload, null, 2)}
        </pre>
      </details>
    </div>
  );
}

/**
 * The install contact the wizard named, and whether it has reached Shift.
 * Shown beside the report payload so the one thing that does leave with a
 * person's name on it is inspectable in the same place as everything else
 * (docs/27 §`POST /api/v1/contact`) — it rides its own call, never the report.
 */
function ContactDetail({
  contact,
}: {
  contact: FleetContactDelivery | null;
}): React.ReactElement | null {
  if (contact === null) return null;
  const badge = CONTACT_BADGE[contact.status];
  return (
    <div className="space-y-3" data-testid="fleet-contact">
      <dl>
        <Row label="Install contact">
          <StatusBadge variant={badge.variant}>{badge.label}</StatusBadge>
        </Row>
        {contact.sent_at !== null && <Row label="Sent at">{relativeTime(contact.sent_at)}</Row>}
        {contact.status !== 'sent' && contact.next_attempt_at !== null && (
          <Row label="Next attempt">{new Date(contact.next_attempt_at).toLocaleString()}</Row>
        )}
        {contact.attempt_count > 1 && <Row label="Attempts">{contact.attempt_count}</Row>}
        {contact.error_message !== null && contact.status !== 'sent' && (
          <Row label="Last error">
            <span className="text-[hsl(var(--status-red-text))]">{contact.error_message}</span>
          </Row>
        )}
      </dl>
      <details className="group">
        <summary className="cursor-pointer text-sm font-medium text-foreground select-none">
          Exact contact sent to Shift
        </summary>
        <pre
          data-testid="fleet-contact-payload"
          className="mt-2 max-h-80 overflow-auto rounded-gl-lg border border-border bg-muted/40 p-3 text-xs text-foreground"
        >
          {JSON.stringify(contact.contact_payload, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-t border-border first:border-t-0 text-sm">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className="font-medium text-foreground min-w-0 truncate text-right">{children}</dd>
    </div>
  );
}
